import { z } from 'zod';
import { macroTriggerSchema } from '../macroTree';
import { blobAssetSchema } from '../macroCalibration/jobs';
import { sha256HexSchema } from './canonicalJson';
import { macroInputModePolicyOverrideSchema } from './inputModePolicy';
import { inputEditSchema } from './sourceGraph';
import { rawRegionRectSchema } from './rawPackage';
import { type SourceGraphIdentity, sourceGraphIdentitySchema } from './sourceIdentity';
export { sourceGraphIdentitySchema, type SourceGraphIdentity } from './sourceIdentity';

const campaignId = z.string().min(1).max(128);
const revision = z.number().int().nonnegative().safe();

/** Identity must agree with the enclosing manifest, artifact or certificate target. */
export function validateSourceIdentity(value: {
  sourceIdentity?: SourceGraphIdentity;
  target?: { campaignId?: string; trigger?: string; variant?: string; rev?: number };
  campaignId?: string; trigger?: string; variant?: string; rev?: number;
}, context: z.RefinementCtx): void {
  const target = value.target ?? value;
  const identity = value.sourceIdentity;
  if (target.variant === 'source-graph' && !identity) {
    context.addIssue({ code: z.ZodIssueCode.custom, path: ['sourceIdentity'], message: 'Source graph requires its saved identity' });
  } else if (identity && ['campaignId', 'trigger', 'variant', 'rev'].some(key =>
    identity[key as keyof SourceGraphIdentity] !== target[key as keyof typeof target])) {
    context.addIssue({ code: z.ZodIssueCode.custom, path: ['sourceIdentity'], message: 'Source identity must match the target' });
  }
}

export const recordingPackageSchema = z.object({ packageId: z.string().uuid(), manifestSha256: sha256HexSchema }).strict();
export const recordingPackagesSchema = z.array(recordingPackageSchema).min(1).max(64)
  .refine(rows => new Set(rows.map(row => row.packageId)).size === rows.length, 'Package ids must be unique');
export const recordingNodeAuthoringSchema = z.object({
  screenStateId: z.string().uuid().nullable(), included: z.boolean(), name: z.string().min(1).max(80),
  edits: z.array(inputEditSchema).max(10_000),
  regions: z.array(z.object({ regionId: z.string().uuid(), rect: rawRegionRectSchema.optional() }).strict()).max(64),
}).strict();
export type RecordingNodeAuthoring = z.infer<typeof recordingNodeAuthoringSchema>;
export const recordingAppendRequestSchema = z.object({ operationId: z.string().uuid(), expectedRev: revision,
  packages: recordingPackagesSchema,
  authoring: z.array(z.object({ packageId: z.string().uuid(), fromUs: z.number().int().nonnegative().safe(),
    toUs: z.number().int().positive().safe(), policyOverride: macroInputModePolicyOverrideSchema,
    nodes: z.array(recordingNodeAuthoringSchema).max(4_096).optional(),
  }).strict().refine(value => value.toUs > value.fromUs, 'Authoring range must be nonempty')).max(64).optional(),
}).strict().superRefine((value, context) => {
  const ids = value.authoring?.map(row => row.packageId) ?? [];
  if (new Set(ids).size !== ids.length || ids.some(id => !value.packages.some(pkg => pkg.packageId === id))) {
    context.addIssue({ code: z.ZodIssueCode.custom, path: ['authoring'], message: 'Authoring settings must name unique appended packages' });
  }
  for (const [authoringIndex, authoring] of (value.authoring ?? []).entries()) {
    const screenIds = authoring.nodes?.map(node => node.screenStateId).filter((id): id is string => id !== null) ?? [];
    if (new Set(screenIds).size !== screenIds.length || (authoring.nodes?.filter(node => node.screenStateId === null).length ?? 0) > 1) {
      context.addIssue({ code: z.ZodIssueCode.custom, path: ['authoring', authoringIndex, 'nodes'],
        message: 'Authored nodes must identify unique screen states and at most one implicit state' });
    }
    for (const [nodeIndex, node] of (authoring.nodes ?? []).entries()) {
      if (node.edits.some(edit => edit.op !== 'insert' && edit.packageId !== authoring.packageId)) {
        context.addIssue({ code: z.ZodIssueCode.custom, path: ['authoring', authoringIndex, 'nodes', nodeIndex, 'edits'],
          message: 'Authored edits must target their recording package' });
      }
    }
  }
});
export const recordingAppendResultSchema = z.object({ campaignId, trigger: macroTriggerSchema,
  rev: revision.refine(value => value > 0), addedNodeIds: z.array(z.string().uuid()).max(4096) }).strict();
export type RecordingAppendRequest = z.infer<typeof recordingAppendRequestSchema>;
export type RecordingAppendResult = z.infer<typeof recordingAppendResultSchema>;

const ingestIdentity = { requestId: z.string().min(1).max(128), campaignId, packageId: z.string().uuid() };
export const packageIngestStateSchema = z.discriminatedUnion('state', [
  z.object({ ...ingestIdentity, state: z.literal('uploading') }).strict(),
  z.object({ ...ingestIdentity, state: z.literal('ingested'), manifestSha256: sha256HexSchema }).strict(),
  z.object({ ...ingestIdentity, state: z.literal('failed'), error: z.string().min(1).max(1024), retryable: z.boolean() }).strict(),
]);
export type PackageIngestState = z.infer<typeof packageIngestStateSchema>;
export const campaignMacroRuntimeSchema = z.discriminatedUnion('mode', [
  z.object({ mode: z.literal('legacy-tree'), revision }).strict(),
  z.object({ mode: z.literal('package'), packageSha256: sha256HexSchema, sourceRev: revision.refine(value => value > 0) }).strict(),
  z.object({ mode: z.literal('disabled'), revision }).strict(),
]);
export type CampaignMacroRuntime = z.infer<typeof campaignMacroRuntimeSchema>;
export const activateMacroRequestSchema = z.object({ expectedRuntimeRevision: revision,
  sourceRev: revision.refine(value => value > 0), packageSha256: sha256HexSchema }).strict();
export type ActivateMacroRequest = z.infer<typeof activateMacroRequestSchema>;
export const macroRuntimeResponseSchema = z.object({ runtimeRevision: revision, runtime: campaignMacroRuntimeSchema.nullable() }).strict();
export const macroRuntimeSelectionRequestSchema = z.object({ expectedRuntimeRevision: revision,
  selection: campaignMacroRuntimeSchema }).strict();

export const compileDiagnosticSchema = z.object({ severity: z.enum(['error', 'warning', 'info']),
  code: z.string().min(1).max(128), nodeId: z.string().max(128).optional(), seq: revision.optional(),
  message: z.string().max(1024) }).strict();
export type CompileDiagnosticBody = z.infer<typeof compileDiagnosticSchema>;
export const calibrationEvidenceCaseSchema = z.object({
  caseId: z.string().min(1).max(128), kind: z.enum(['anchor', 'transform', 'temporal', 'negative']),
  source: blobAssetSchema, rendered: blobAssetSchema, offsetUs: z.number().int().safe().nullable(),
  transform: z.object({ id: z.string().min(1).max(128), version: z.string().min(1).max(128),
    parameters: z.record(z.union([z.number().finite(), z.string().max(512), z.boolean()])) }).strict().nullable(),
  rawDifferencePercent: z.number().finite().min(0).max(100), included: z.boolean(),
  exclusionReason: z.enum(['outside_window', 'extreme_difference', 'negative_control',
    'synthetic_ceiling', 'captured_outlier', 'structure_lost', 'held_out', 'diagnostic']).nullable(),
}).strict().superRefine((value, context) => {
  let expected: typeof value.exclusionReason = value.exclusionReason;
  if (value.kind === 'negative') expected = 'negative_control';
  if (value.kind === 'temporal') {
    if (value.offsetUs === null || Math.abs(value.offsetUs) > 100_000) expected = 'outside_window';
    else if (value.rawDifferencePercent > 10) expected = 'extreme_difference';
    else expected = value.exclusionReason === 'structure_lost' ? 'structure_lost' : null;
  }
  if (value.exclusionReason !== expected || value.included !== (expected === null)) {
    context.addIssue({ code: z.ZodIssueCode.custom, message: 'Evidence inclusion must follow the exact window and raw-score policy' });
  }
  if ((value.kind === 'transform' && value.transform === null)
    || (['anchor', 'temporal'].includes(value.kind) && value.transform !== null)) {
    context.addIssue({ code: z.ZodIssueCode.custom, path: ['transform'], message: 'Transformed cases require a pinned transform' });
  }
});
export type CalibrationEvidenceCase = z.infer<typeof calibrationEvidenceCaseSchema>;
export const sourceGraphEvidenceHeaderSchema = z.object({ sourceIdentity: sourceGraphIdentitySchema,
  artifactDigest: sha256HexSchema, nodeId: z.string().uuid(), packageId: z.string().uuid(), regionId: z.string().uuid(),
  scoringRule: z.string().min(1).max(128), radiusUs: z.literal(100_000), temporalExclusionAbovePercent: z.literal(10),
  recommendedThresholdPercent: z.number().int().min(0).max(100) }).strict();
export const sourceGraphEvidenceSchema = sourceGraphEvidenceHeaderSchema.extend({ cases: z.array(calibrationEvidenceCaseSchema).max(16_384) }).strict();
export const sourceGraphEvidencePageSchema = z.object({ evidence: sourceGraphEvidenceHeaderSchema,
  cases: z.array(calibrationEvidenceCaseSchema).max(128), nextCursor: z.string().min(1).max(512).optional() }).strict();
export type SourceGraphEvidence = z.infer<typeof sourceGraphEvidenceSchema>;
